// sseAlerts.tsx — SSE subscriber + admin notification bell.
//
// Subscribes to /api/admin/sse/alerts (or /api/admin/me-agent/sse/alerts
// for agent dashboards). Auto-reconnects via EventSource defaults.
//
// Two exports:
//   - useSseAlerts({endpoint}) — hook that connects + tracks state.
//   - <SseAlertsBell endpoint=... /> — top-bar bell icon with badge,
//     animated on new arrival + plays a short ding, opens a scrollable
//     dropdown panel on click. The Phase 19 / Batch 5 rebuild added
//     deep-linking, per-row mark-read, and a Mark-all-read button.
//
// (A pre-Phase-13.7 <SseAlertsBanner> stacked-toast variant was
// removed in Phase 19 / Batch 7 — last cleanup of the audit.)
//
// Each alert kind maps to a color tone:
//   - deposit_breach_100pct + agent_suspended  → red (urgent)
//   - deposit_warning_80pct                    → amber (warning)
//   - wallet_balance_changed                   → silent (no banner — too noisy)
//   - match_finished                           → cyan (informational)

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import Icon from '../../components/Icon';
import { adminApi, getAdminAccessToken } from './adminApi';

export type SseAlertKind =
  | 'deposit_warning_80pct'
  | 'deposit_breach_100pct'
  | 'deposit_recovered'
  | 'agent_suspended'
  | 'wallet_balance_changed'
  | 'match_finished'
  | 'rate_changed'
  | 'rate_clamped_cascade'
  | 'admin_action_required';

export interface SseAlert {
  id: number;
  kind: SseAlertKind;
  payload: Record<string, unknown>;
  receivedAt: number;
  dismissed?: boolean;
}

interface UseSseAlertsArgs {
  endpoint: string;        // e.g. '/api/admin/sse/alerts'
  enabled?: boolean;       // default true
  maxKeep?: number;        // default 50
}

export function useSseAlerts({ endpoint, enabled = true, maxKeep = 50 }: UseSseAlertsArgs) {
  const [alerts, setAlerts] = useState<SseAlert[]>([]);
  const [connected, setConnected] = useState(false);
  const esRef = useRef<EventSource | null>(null);
  // Read on every render so login/logout (the token appearing or clearing)
  // re-runs the effect below — `token` is one of its deps.
  const token = getAdminAccessToken();

  useEffect(() => {
    // #71 — a logged-OUT session must neither RECEIVE nor POLL for
    // notifications. With no token we never open the stream: this both
    // honours the rule and stops EventSource from spinning an endless 401
    // auto-reconnect loop against requireAdmin. When the token clears on
    // logout/expiry this effect re-runs (token is a dep) and the cleanup
    // returned below closes any stream that was open. Sending the token
    // afresh on each reconnect also re-authorises the long-lived stream,
    // closing the "expired token keeps streaming" gap.
    if (!enabled || !token) { setConnected(false); return; }
    let cancelled = false;
    const sep = endpoint.includes('?') ? '&' : '?';
    const url = `${endpoint}${sep}token=${encodeURIComponent(token)}`;
    const es = new EventSource(url, { withCredentials: true });
    esRef.current = es;
    es.onopen = () => { if (!cancelled) setConnected(true); };
    es.onerror = () => { if (!cancelled) setConnected(false); };

    const handler = (e: MessageEvent) => {
      if (cancelled) return;
      try {
        const payload = JSON.parse(e.data || '{}');
        const id = Number((e as MessageEvent).lastEventId) || Date.now();
        const kind = (e.type as SseAlertKind) || 'admin_action_required';
        const alert: SseAlert = { id, kind, payload, receivedAt: Date.now() };
        setAlerts(prev => {
          const filtered = prev.filter(a => a.id !== id);
          return [alert, ...filtered].slice(0, maxKeep);
        });
      } catch {}
    };

    const kinds: SseAlertKind[] = [
      'deposit_warning_80pct', 'deposit_breach_100pct', 'deposit_recovered',
      'agent_suspended', 'wallet_balance_changed', 'match_finished',
      'rate_changed', 'rate_clamped_cascade', 'admin_action_required',
    ];
    kinds.forEach(k => es.addEventListener(k, handler as EventListener));

    return () => {
      cancelled = true;
      kinds.forEach(k => es.removeEventListener(k, handler as EventListener));
      es.close();
      esRef.current = null;
    };
  }, [endpoint, enabled, maxKeep, token]);

  const dismiss = useCallback((id: number) => {
    setAlerts(prev => prev.map(a => a.id === id ? { ...a, dismissed: true } : a));
  }, []);

  const dismissAll = useCallback(() => {
    setAlerts(prev => prev.map(a => ({ ...a, dismissed: true })));
  }, []);

  return { alerts, connected, dismiss, dismissAll };
}

// ─────────────────────────────────────────────────────────────────
//  Visual constants (shared between bell-panel + legacy banner)
// ─────────────────────────────────────────────────────────────────

const KIND_TONE: Record<SseAlertKind, { tone: string; visible: boolean }> = {
  deposit_breach_100pct:   { tone: 'red',    visible: true  },
  agent_suspended:         { tone: 'red',    visible: true  },
  deposit_warning_80pct:   { tone: 'amber',  visible: true  },
  deposit_recovered:       { tone: 'cyan',   visible: true  },
  admin_action_required:   { tone: 'red',    visible: true  },
  rate_changed:            { tone: 'cyan',   visible: true  },
  rate_clamped_cascade:    { tone: 'amber',  visible: true  },
  match_finished:          { tone: 'cyan',   visible: false },
  wallet_balance_changed:  { tone: 'cyan',   visible: false },
};

const TONE_CARD: Record<string, string> = {
  red:   'bg-rose-50/80 dark:bg-rose-950/60 border-rose-300/70 dark:border-rose-800/70 text-rose-900 dark:text-rose-100',
  amber: 'bg-amber-50/80 dark:bg-amber-950/60 border-amber-300/70 dark:border-amber-800/70 text-amber-900 dark:text-amber-100',
  cyan:  'bg-cyan-50/80 dark:bg-cyan-950/60 border-cyan-300/70 dark:border-cyan-800/70 text-cyan-900 dark:text-cyan-100',
};
const TONE_TITLE: Record<string, string> = {
  red:   'text-rose-700 dark:text-rose-200',
  amber: 'text-amber-700 dark:text-amber-200',
  cyan:  'text-cyan-700 dark:text-cyan-200',
};

// ─────────────────────────────────────────────────────────────────
//  Bell ring sound (Web Audio synthesis — no asset)
// ─────────────────────────────────────────────────────────────────

let _audioCtx: AudioContext | null = null;
let _audioPrimed = false;

function getAudioCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (_audioCtx) return _audioCtx;
  const Ctx = (window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
  if (!Ctx) return null;
  try {
    _audioCtx = new Ctx();
    return _audioCtx;
  } catch { return null; }
}

/**
 * Prime the AudioContext on the first user gesture anywhere on the page.
 * Browsers require this — an AudioContext created without a prior user
 * gesture starts in 'suspended' state and silently drops .play() calls.
 * Once primed (resume() called from a gesture handler), all subsequent
 * dings work for the rest of the session.
 */
function primeAudioOnFirstGesture() {
  if (_audioPrimed) return;
  if (typeof window === 'undefined') return;
  const onGesture = () => {
    if (_audioPrimed) return;
    const ctx = getAudioCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      ctx.resume().then(() => { _audioPrimed = true; }).catch(() => {});
    } else {
      _audioPrimed = true;
    }
    window.removeEventListener('pointerdown', onGesture);
    window.removeEventListener('keydown', onGesture);
    window.removeEventListener('touchstart', onGesture);
  };
  window.addEventListener('pointerdown', onGesture, { once: true, passive: true });
  window.addEventListener('keydown',     onGesture, { once: true, passive: true });
  window.addEventListener('touchstart',  onGesture, { once: true, passive: true });
}

function playDing() {
  const ctx = getAudioCtx();
  if (!ctx) return;
  // Best-effort resume in case prime() hasn't fired yet (no harm if it has).
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  // Two harmonics + short exponential decay — sounds like a soft ding.
  // Volume bumped to 0.35 (was 0.18 — too quiet on laptop speakers).
  const now = ctx.currentTime;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.001, now);
  g.gain.exponentialRampToValueAtTime(0.35, now + 0.01);
  g.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
  g.connect(ctx.destination);

  const o1 = ctx.createOscillator();
  o1.type = 'sine'; o1.frequency.value = 1320; // E6
  o1.connect(g); o1.start(now); o1.stop(now + 0.65);

  const o2 = ctx.createOscillator();
  o2.type = 'sine'; o2.frequency.value = 1760; // A6
  o2.connect(g); o2.start(now); o2.stop(now + 0.55);
}

// ─────────────────────────────────────────────────────────────────
//  Phase 19 / Batch 5 #8 — deep-link mapper.
//  Each kind + payload combination resolves to a URL the admin
//  should land on when they click the alert row. Returns null for
//  alerts we don't have a sensible destination for (the row is
//  still clickable to mark-as-read, just doesn't navigate).
//
//  Most alert payloads include either `agentId` (the agent the
//  alert is ABOUT — e.g. an agent crossing -80%), `targetAgentId`
//  (the recipient when an admin acted ON another agent — e.g.
//  parent suspending a sub-agent), or `targetUserId` (player
//  affected by an admin action). We pick whichever is present.
// ─────────────────────────────────────────────────────────────────

interface AlertPayloadShape {
  agentId?: number | string | null;
  targetAgentId?: number | string | null;
  targetUserId?: number | string | null;
  actorAgentId?: number | string | null;
  action?: string;
}

function deepLinkFor(kind: SseAlertKind, payload: Record<string, unknown>): string | null {
  const p = payload as AlertPayloadShape;
  const agentId      = p.agentId       != null ? String(p.agentId)       : null;
  const targetAgent  = p.targetAgentId != null ? String(p.targetAgentId) : null;
  const targetUser   = p.targetUserId  != null ? String(p.targetUserId)  : null;

  switch (kind) {
    case 'deposit_warning_80pct':
    case 'deposit_breach_100pct':
    case 'deposit_recovered':
    case 'agent_suspended':
      return agentId ? `/people/agents/${agentId}` : null;

    case 'wallet_balance_changed':
      return agentId ? `/people/agents/${agentId}` : null;

    case 'admin_action_required': {
      // Most concrete: targetUser (player suspend/ban) → user detail.
      if (targetUser)  return `/people/players/${targetUser}`;
      // Then targetAgent (parent acted on sub-agent) → agent detail.
      if (targetAgent) return `/people/agents/${targetAgent}`;
      // Fallback: actor's agent (less useful but better than nothing).
      if (agentId)     return `/people/agents/${agentId}`;
      return null;
    }

    case 'rate_changed':
    case 'rate_clamped_cascade':
      return '/operations/audit';

    case 'match_finished':
    default:
      return null;
  }
}

// ─────────────────────────────────────────────────────────────────
//  Phase 19 / Batch 5 — read-state model.
//
//  An alert is READ if EITHER:
//    (a) its id is at or below `lastReadId` (the "Mark all read"
//        high-water; one number per actor, persisted to backend), OR
//    (b) its id is in the per-alert `readSet` (a small JSON array
//        of explicitly-clicked-read ids ABOVE the high-water).
//
//  Why both? "Mark all read" needs to be cheap (don't bloat localStorage
//  with N ids). Per-alert clicks need to decrement the badge by exactly
//  ONE — not collapse the count to 0 by raising a monotonic high-water.
//
//  The set is capped at 500 ids — enough for a normal admin's history
//  (we only ever surface the most recent ~50 anyway). When the high-
//  water rises (Mark all read), we prune any ids ≤ high-water from the
//  set since they're now redundant.
// ─────────────────────────────────────────────────────────────────

const READ_MARK_LS_KEY = 'baali.bell.lastReadId';
const READ_SET_LS_KEY  = 'baali.bell.readSet';
const READ_SET_CAP     = 500;

function readLocalMark(): number {
  if (typeof window === 'undefined') return 0;
  try { return Number(window.localStorage.getItem(READ_MARK_LS_KEY) || '0') | 0; }
  catch { return 0; }
}

function writeLocalMark(id: number) {
  if (typeof window === 'undefined') return;
  try {
    const cur = readLocalMark();
    if (id > cur) window.localStorage.setItem(READ_MARK_LS_KEY, String(id));
  } catch { /* localStorage may be disabled — best-effort */ }
}

function readLocalSet(): Set<number> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(READ_SET_LS_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.map((n: unknown) => Number(n) | 0).filter((n) => n > 0));
  } catch { return new Set(); }
}

function writeLocalSet(s: Set<number>) {
  if (typeof window === 'undefined') return;
  try {
    // Keep only the most-recent (largest) READ_SET_CAP ids — the
    // bell only ever surfaces the most recent ~50 anyway.
    const arr = Array.from(s).sort((a, b) => b - a).slice(0, READ_SET_CAP);
    window.localStorage.setItem(READ_SET_LS_KEY, JSON.stringify(arr));
  } catch { /* best-effort */ }
}

// ─────────────────────────────────────────────────────────────────
//  <SseAlertsBell> — top-bar bell + dropdown panel
// ─────────────────────────────────────────────────────────────────

export function SseAlertsBell({ endpoint }: { endpoint: string }) {
  const { alerts, dismiss, dismissAll } = useSseAlerts({ endpoint });
  const navigate = useNavigate();

  // Phase 19 / Batch 5: explicit "read" model — see the comment-block
  // header above READ_MARK_LS_KEY. localStorage hydrates on mount so
  // the badge count is right INSTANTLY even before SSE/GET finish
  // (fixes N9 — badge race dropping to blank then re-appearing). The
  // backend GET is then queried to pick up cross-device marks;
  // whichever high-water is higher wins.
  const [lastReadId, setLastReadId] = useState<number>(() => readLocalMark());
  const [readSet,    setReadSet]    = useState<Set<number>>(() => readLocalSet());
  useEffect(() => {
    // #71 — logged out (no token) means a guaranteed 401; don't even ask.
    if (!getAdminAccessToken()) return;
    let alive = true;
    adminApi
      .get<{ lastReadAlertId: number }>(endpoint.replace('/alerts', '/alerts/read-mark'))
      .then((r) => {
        if (!alive) return;
        const remote = Number(r?.lastReadAlertId || 0) | 0;
        setLastReadId((cur) => Math.max(cur, remote));
        if (remote > readLocalMark()) writeLocalMark(remote);
      })
      .catch(() => { /* no-op: localStorage continues to be the source of truth */ });
    return () => { alive = false; };
  }, [endpoint]);

  // Visible (non-dismissed, kind that we actually surface).
  const visible = useMemo(
    () => alerts.filter(a => !a.dismissed && KIND_TONE[a.kind]?.visible),
    [alerts]
  );
  // Per-alert read predicate: id ≤ high-water OR id ∈ explicit set.
  const isRead = useCallback(
    (id: number) => id <= lastReadId || readSet.has(id),
    [lastReadId, readSet]
  );
  // UNREAD = visible alerts where the predicate is false.
  const unread = useMemo(
    () => visible.filter(a => !isRead(a.id)).length,
    [visible, isRead]
  );

  // Push the high-water to the backend in the background. Fire-and-forget;
  // the localStorage write already happened so the UX has updated.
  const persistMark = useCallback((id: number) => {
    if (id <= 0) return;
    adminApi
      .post(endpoint.replace('/alerts', '/alerts/mark-read'), { upToId: id })
      .catch(() => { /* network blip — localStorage holds, retry on next mark */ });
  }, [endpoint]);

  // Mark a single alert as read. Adds to the explicit set (NOT the
  // high-water) so the badge decrements by exactly ONE per click, not
  // by however many alerts share a lower id.
  const markOneRead = useCallback((alertId: number) => {
    setReadSet((cur) => {
      if (cur.has(alertId) || alertId <= lastReadId) return cur;  // already read
      const next = new Set(cur);
      next.add(alertId);
      writeLocalSet(next);
      return next;
    });
  }, [lastReadId]);

  // Mark every currently-visible alert as read by raising the high-water
  // to the topmost (newest) id. Also prunes the per-alert set since
  // those ids are now implicitly read by the high-water.
  const markAllRead = useCallback(() => {
    if (visible.length === 0) return;
    const top = visible[0].id;  // visible is newest-first
    setLastReadId((cur) => {
      const next = Math.max(cur, top);
      if (next > cur) {
        writeLocalMark(next);
        persistMark(next);
        // Prune set entries now-redundant by the new high-water.
        setReadSet((s) => {
          const pruned = new Set<number>();
          s.forEach((id) => { if (id > next) pruned.add(id); });
          if (pruned.size !== s.size) writeLocalSet(pruned);
          return pruned;
        });
      }
      return next;
    });
  }, [visible, persistMark]);

  const [open, setOpen] = useState(false);
  const [animating, setAnimating] = useState(false);
  const lastSeenIdRef = useRef<number | null>(null);
  const animTimerRef = useRef<number | null>(null);
  // Phase 18: when the panel is open and the visible-alerts list goes
  // empty (either because the user clicked Clear all, or because they
  // opened it with nothing to read), auto-close after a short delay.
  // Without this, the "No new notifications" empty card sits there
  // until the user manually clicks the bell again or clicks outside —
  // a UX wart users have complained about.
  const autoCloseTimerRef = useRef<number | null>(null);

  // Detect a NEW alert (top-of-list id changed AND it's newer than the last
  // time we ever saw). Animate + play sound only when the panel is closed.
  useEffect(() => {
    if (visible.length === 0) return;
    const newestId = visible[0].id;
    if (lastSeenIdRef.current === null) {
      // First sample after mount — don't animate / play; just record.
      lastSeenIdRef.current = newestId;
      return;
    }
    if (newestId === lastSeenIdRef.current) return;
    lastSeenIdRef.current = newestId;
    if (open) return;                  // panel is showing → no need to attract attention
    setAnimating(true);
    playDing();
    if (animTimerRef.current) window.clearTimeout(animTimerRef.current);
    animTimerRef.current = window.setTimeout(() => setAnimating(false), 2400);
  }, [visible, open]);

  useEffect(() => () => { if (animTimerRef.current) window.clearTimeout(animTimerRef.current); }, []);

  // Phase 18: auto-close panel when it's open and there's nothing to
  // show. Fires 1.5s after either (a) the user opens the bell with an
  // empty queue, or (b) the user clicks Clear all (which drops unread
  // to 0). Clear any pending close timer when the panel re-fills or
  // closes manually.
  useEffect(() => {
    // Cancel any pending auto-close
    if (autoCloseTimerRef.current) {
      window.clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
    // Phase 18 + Batch 5: auto-close on truly-empty panel.
    // Was keyed on `unread === 0` but Batch 5 split unread vs visible —
    // an admin who marked everything read still wants to scroll the
    // history. Auto-close only when there's literally nothing visible.
    if (open && visible.length === 0) {
      autoCloseTimerRef.current = window.setTimeout(() => {
        setOpen(false);
        autoCloseTimerRef.current = null;
      }, 1500);
    }
    return () => {
      if (autoCloseTimerRef.current) {
        window.clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
  }, [open, visible.length]);

  // Prime the audio context on the first user gesture so the bell-ring
  // ding can actually play. Browsers block AudioContext.start() until
  // a gesture has been recorded; without this listener the very first
  // ding (and sometimes all of them) is silent.
  useEffect(() => { primeAudioOnFirstGesture(); }, []);

  function toggle() {
    setOpen(o => !o);
    setAnimating(false);
    // Mark the newest alert as "seen" so it doesn't re-trigger animation
    if (visible[0]) lastSeenIdRef.current = visible[0].id;
  }

  // Stack-style panel container (matches the legacy SseAlertsBanner
  // look: pinned to top-right of the viewport, fits to safe-area, has
  // its own internal scroll when there are many alerts, with the
  // Clear-all pill at the BOTTOM of the stack — same place users were
  // used to). Width is responsive: full-width on phones (with small
  // side gutters via env(safe-area-inset-right)), capped on desktop.
  const stackStyle: React.CSSProperties = {
    paddingTop:    'max(0.5rem, env(safe-area-inset-top))',
    paddingRight:  'max(0.5rem, env(safe-area-inset-right))',
    paddingLeft:   'max(0.5rem, env(safe-area-inset-left))',
    // No height cap on the OUTER container — let it size to fit its
    // content (so few notifications = small panel, lots = scrollable
    // panel, see scroll-container's max-h). Without this, the outer
    // would stretch to fill flex space and block clicks on UI to its
    // left/below even when the actual content is small.
  };

  return (
    <div className="relative">
      <button
        onClick={toggle}
        title={unread > 0 ? `${unread} notification${unread === 1 ? '' : 's'}` : 'No notifications'}
        aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ''}`}
        className={`relative grid place-items-center w-9 h-9 rounded-full
                    bg-neutral-100 hover:bg-neutral-200
                    dark:bg-white/5 dark:hover:bg-white/10
                    transition shrink-0 ${animating ? 'sse-bell-ring' : ''}`}
      >
        <Icon name="notifications" size={18} className="text-neutral-600 dark:text-neutral-300" />
        {unread > 0 && (
          <span
            className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full
                        bg-rose-600 text-white text-[10px] font-bold tabular-nums
                        flex items-center justify-center
                        ring-2 ring-white dark:ring-neutral-900
                        ${animating ? 'animate-pulse' : ''}`}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {/* Stack panel — opens only when the bell is clicked. Pinned to
          top-right of the VIEWPORT (not anchored to the bell) so the
          old toast-banner layout is preserved exactly. Scrollable
          internally when there are many alerts. */}
      <AnimatePresence>
        {open && (
          <>
            {/* Transparent backdrop catches outside-clicks to close. */}
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit   ={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.15 }}
              // Width: at most 24rem on desktop; on phones, full width
              // minus 1rem total gutters so cards never get clipped.
              // Outer container is pointer-events-none so empty padding
              // around the panel passes clicks through to the backdrop.
              className="fixed top-0 right-0 z-50 w-full max-w-sm flex flex-col gap-2 pointer-events-none"
              style={stackStyle}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Scrollable inner stack (capped to fit screen).
                  pointer-events-auto + overscroll-contain on the scroll
                  container itself: pointer-events-auto so wheel/touch
                  events are captured by THIS div (not the page below
                  via pointer-events:none of the parent), and
                  overscroll-contain so reaching the top/bottom doesn't
                  chain to the body and scroll the page. touch-pan-y
                  hints to mobile that this surface is vertically
                  scrollable. */}
              {/* Batch 5 #7: "Mark all as read" button at the TOP of
                  the panel. Optimistic — clears the badge instantly
                  (localStorage write + state update), then fires
                  the backend POST in the background. Only renders
                  when there's something unread to mark. */}
              {unread > 0 && (
                <div className="pointer-events-auto flex items-center justify-between gap-2">
                  <span className="text-[10.5px] uppercase tracking-wider font-bold text-neutral-600 dark:text-neutral-300 px-1">
                    {unread} unread · {visible.length} total
                  </span>
                  <button
                    onClick={markAllRead}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full
                               text-[11px] font-bold uppercase tracking-wider
                               bg-violet-600 text-white hover:bg-violet-500
                               shadow-[0_2px_10px_rgba(139,92,246,0.45)]
                               transition active:scale-95"
                    aria-label="Mark all notifications as read"
                  >
                    <Icon name="done_all" size={12} /> Mark all read
                  </button>
                </div>
              )}

              <div
                className="pointer-events-auto overflow-y-auto overscroll-contain touch-pan-y flex flex-col gap-2"
                style={{
                  // Hard cap at 45% of viewport height. If the cards
                  // total less than this, the div shrinks to fit them
                  // (no flex-1 → no force-stretch). If they overflow,
                  // the inner scroll kicks in.
                  maxHeight: '45dvh',
                }}
                onWheel={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
              >
                {visible.length === 0 ? (
                  <div className="pointer-events-auto rounded-2xl bg-white/85 dark:bg-neutral-900/85 backdrop-blur-xl border border-neutral-200 dark:border-white/10 shadow-md px-4 py-6 text-center">
                    <Icon name="check_circle" size={28} className="text-emerald-400 mx-auto mb-2" />
                    <div className="text-[12.5px] text-neutral-600 dark:text-white/65 font-medium">No new notifications</div>
                  </div>
                ) : (
                  visible.map((a) => {
                    const tone = KIND_TONE[a.kind]?.tone || 'cyan';
                    const card  = TONE_CARD[tone] || TONE_CARD.cyan;
                    const title = TONE_TITLE[tone] || TONE_TITLE.cyan;
                    const msg = String((a.payload as { message?: string })?.message
                                     || a.kind.replace(/_/g, ' '));
                    // Batch 5 #8 / N8: per-row read state + click-to-deep-link.
                    const isUnread = !isRead(a.id);
                    const target   = deepLinkFor(a.kind, a.payload);
                    const onRowClick = () => {
                      // Always mark THIS row as read on click — even if
                      // there's no destination to navigate to.
                      markOneRead(a.id);
                      if (target) {
                        setOpen(false);
                        navigate(target);
                      }
                    };
                    return (
                      <div
                        key={a.id}
                        role={target ? 'link' : 'button'}
                        tabIndex={0}
                        onClick={onRowClick}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onRowClick(); }}
                        className={`pointer-events-auto pr-2 pl-3 py-2 rounded-lg border shadow-md backdrop-blur-xl text-xs flex items-start gap-2 cursor-pointer transition
                                    hover:brightness-105 dark:hover:brightness-125
                                    ${card}
                                    ${isUnread ? '' : 'opacity-70'}`}
                      >
                        {/* Cyan unread dot — visible only when this row
                            is above the read mark. Provides at-a-glance
                            scan of which alerts are new. */}
                        <span
                          aria-hidden="true"
                          className={`mt-1.5 shrink-0 w-2 h-2 rounded-full transition ${
                            isUnread
                              ? 'bg-cyan-500 shadow-[0_0_8px_rgba(34,211,238,0.6)]'
                              : 'bg-transparent'
                          }`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className={`font-extrabold text-[11px] uppercase tracking-wider mb-0.5 inline-flex items-center gap-1 ${title}`}>
                            {a.kind.replace(/_/g, ' ')}
                            {target && <Icon name="open_in_new" size={10} className="opacity-70" />}
                          </div>
                          <div className="break-words font-medium">{msg}</div>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); dismiss(a.id); }}
                          className="shrink-0 opacity-70 hover:opacity-100 text-base leading-none -mr-1"
                          aria-label="dismiss"
                        >×</button>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Clear-all pill at the BOTTOM of the stack — same place
                  the legacy SseAlertsBanner had it. Always shown when
                  panel is open so the user can dismiss everything in
                  one click without scrolling all the way down. */}
              {visible.length > 0 && (
                <div className="pointer-events-auto flex justify-end pt-1">
                  <button
                    onClick={dismissAll}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full
                               text-[11px] font-bold uppercase tracking-wider
                               bg-white/70 dark:bg-neutral-900/70 backdrop-blur-xl
                               text-neutral-800 dark:text-neutral-100
                               border border-neutral-300/70 dark:border-neutral-700/70
                               shadow-md hover:bg-white/90 dark:hover:bg-neutral-900/90
                               transition active:scale-95"
                    aria-label="Clear all notifications"
                  >
                    Clear all{visible.length > 1 ? ` (${visible.length})` : ''}
                  </button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Bell-ring keyframe injected once via a singleton style tag. Tailwind
          doesn't ship a 'wiggle' / 'bell' animation, so we self-host it. */}
      <BellKeyframes />
    </div>
  );
}

function BellKeyframes() {
  // Singleton — injected once into <head>. Re-inserts safely if removed.
  useEffect(() => {
    const id = 'sse-bell-keyframes';
    if (document.getElementById(id)) return;
    const s = document.createElement('style');
    s.id = id;
    s.textContent = `
      @keyframes sseBellRing {
        0%   { transform: rotate(0); }
        15%  { transform: rotate(14deg); }
        30%  { transform: rotate(-12deg); }
        45%  { transform: rotate(10deg); }
        60%  { transform: rotate(-8deg); }
        75%  { transform: rotate(6deg); }
        90%  { transform: rotate(-3deg); }
        100% { transform: rotate(0); }
      }
      .sse-bell-ring { animation: sseBellRing 0.6s ease-in-out 0s 4; transform-origin: 50% 28%; }
    `;
    document.head.appendChild(s);
  }, []);
  return null;
}

// (Phase 19 / Batch 7: <SseAlertsBanner> legacy stacked-toast banner
//  removed here. It was orphaned since Phase 13.7 — the admin shell
//  switched to <SseAlertsBell> in the top bar, and no callers
//  remained. Final cleanup of the brutal admin-panel audit.)
